import React from 'react';
import { ICONS } from '../constants';
import { useCredits } from '../contexts';
import { SUBSCRIPTION_TIERS } from '../services/firebase';

interface CreditsDisplayProps {
  compact?: boolean;
  onClick?: () => void;
}

const CreditsDisplay: React.FC<CreditsDisplayProps> = ({ compact = false, onClick }) => {
  const { credits, maxCredits, subscription, loading } = useCredits();

  const tierConfig = SUBSCRIPTION_TIERS[subscription];
  const percentage = Math.min((credits / maxCredits) * 100, 100);

  const getBarColor = () => {
    if (percentage > 50) return 'bg-green-500';
    if (percentage > 20) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getTierColor = () => {
    switch (subscription) {
      case 'creator': return 'text-click-cyan border-click-cyan/30 bg-click-cyan/5';
      case 'viral': return 'text-violet-300 border-violet-400/30 bg-violet-400/5';
      case 'studio': return 'text-yellow-400 border-yellow-400/30 bg-yellow-400/5';
      default: return 'text-gray-400 border-gray-500/30 bg-gray-500/5';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500">
        <ICONS.Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-xs">טוען...</span>
      </div>
    );
  }

  if (compact) {
    return (
      <button
        onClick={onClick}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-full border transition-colors hover:bg-white/5 ${getTierColor()}`}
      >
        <ICONS.Coins className="w-4 h-4" />
        <span className="text-sm font-medium">{credits.toLocaleString()}</span>
      </button>
    );
  }

  return (
    <div 
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-2 rounded-xl border cursor-pointer transition-all hover:bg-white/5 ${getTierColor()}`}
    >
      <div className="flex items-center gap-2">
        {subscription !== 'free' && <ICONS.Crown className="w-4 h-4" />}
        <ICONS.Coins className="w-4 h-4" />
      </div>
      <div className="flex flex-col min-w-[100px]">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="font-medium">{tierConfig.nameHe}</span>
          <span>{credits.toLocaleString()} / {maxCredits.toLocaleString()}</span>
        </div>
        <div className="h-1.5 bg-click-border rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-300 ${getBarColor()}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default CreditsDisplay;
